import { useState, createContext, useCallback, useContext, useEffect, useRef } from 'react'
import { logAction, formatReportNumber, LOG_ACTIONS } from '../utils/logClient'
import { RUBRIC_DRAG_TYPE } from '../utils/rubricDrop'

export const ThemeContext = createContext({
	theme: 'light',
	toggleTheme: () => {},
	setInitialTheme: () => {}
})

export function ThemeContextProvider ({children}) {
	const [theme, setTheme] = useState('light')

	const toggleTheme = useCallback(() => {
    	setTheme(currentTheme => currentTheme === 'light' ? 'dark' : 'light')
  	}, [])

	const setInitialTheme = useCallback((userTheme) => {
		if (userTheme === 'light' || userTheme === 'dark') {
			setTheme(userTheme)
		}
	}, [])

  	return (
		<ThemeContext.Provider value={{
			theme,
			toggleTheme,
			setInitialTheme
		}}>
			{children}
		</ThemeContext.Provider>
	)
}

export const ReportContext = createContext();

export function ReportContextProvider({ children }) {
  const [report, setReport] = useState(null); // null par défaut

  const pickReport = useCallback((chosenReport) => {
    if (!chosenReport) {
      setReport(null);
    } else {
      // On ne stocke que number, year et new
      const { number, year, new: isNew = false } = chosenReport;
      setReport({ number, year, new: isNew });
    }
  }, []);

  return (
    <ReportContext.Provider value={{ report, pickReport }}>
      {children}
    </ReportContext.Provider>
  );
}

export const UserContext = createContext();

export const UserContextProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Valeur initiale à null

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const AdminPageContext = createContext('')

export const AdminPageContextProvider = ({children}) => {
	const [adminPage, setAdminPage] = useState('')

  	return <AdminPageContext.Provider value={{ adminPage, setAdminPage }}>
  			{children}
  		</AdminPageContext.Provider>
}

export const PageContext = createContext('')

export const PageContextProvider = ({children}) => {
	const [page, setPage] = useState()

  	return <PageContext.Provider value={{ page, setPage }}>
  			{children}
  		</PageContext.Provider>
}

// Ajout du SocketContext pour la gestion des WebSockets
export const SocketContext = createContext(null);

export const SocketContextProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);

  const updateSocket = useCallback((newSocket) => {
    setSocket(newSocket);
  }, []);

  return (
    <SocketContext.Provider value={{ socket, setSocket: updateSocket }}>
      {children}
    </SocketContext.Provider>
  );
};

// Contexte pour le défilement vers les rubriques
export const ScrollContext = createContext(null);

export const ScrollContextProvider = ({ children }) => {
  const [targetRubric, setTargetRubric] = useState(null);
  const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState(0);

  const triggerScrollToBottom = useCallback(() => {
    setScrollToBottomTrigger(prev => prev + 1);
  }, []);

  return (
    <ScrollContext.Provider value={{ 
      targetRubric, 
      setTargetRubric,
      scrollToBottomTrigger,
      triggerScrollToBottom
    }}>
      {children}
    </ScrollContext.Provider>
  );
};

// Contexte pour le mode d'affichage (edit/read)
export const ModeContext = createContext(null);

export const ModeContextProvider = ({ children }) => {
  const [mode, setMode] = useState('edit');

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
  }, []);

  const updateMode = useCallback((newMode) => {
    setMode(newMode);
  }, []);

  return (
    <ModeContext.Provider value={{ mode, setMode: updateMode, handleModeChange }}>
      {children}
    </ModeContext.Provider>
  );
};

// Contexte pour gérer les modifications en cours
export const EditingContext = createContext(null);

export const EditingContextProvider = ({ children }) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingRubrics, setEditingRubrics] = useState(new Set());

  // Fonction pour ajouter une rubrique en cours d'édition
  const startEditingRubric = useCallback((rubricId) => {
    setEditingRubrics(prev => new Set([...prev, rubricId]));
    setHasUnsavedChanges(true);
  }, []);

  // Fonction pour retirer une rubrique en cours d'édition
  const stopEditingRubric = useCallback((rubricId) => {
    setEditingRubrics(prev => {
      const newSet = new Set(prev);
      newSet.delete(rubricId);

      // Si plus aucune rubrique en cours d'édition, on n'a plus de modifications non sauvées
      if (newSet.size === 0) {
        setHasUnsavedChanges(false);
      }

      return newSet;
    });
  }, []);

  // Fonction pour réinitialiser l'état (quand on quitte le document)
  const resetEditingState = useCallback(() => {
    setHasUnsavedChanges(false);
    setEditingRubrics(new Set());
  }, []);

  // Fonction pour vérifier s'il y a des modifications en cours
  const checkHasUnsavedChanges = useCallback(() => {
    return hasUnsavedChanges || editingRubrics.size > 0;
  }, [hasUnsavedChanges, editingRubrics.size]);

  return (
    <EditingContext.Provider value={{
      hasUnsavedChanges,
      editingRubrics,
      startEditingRubric,
      stopEditingRubric,
      resetEditingState,
      checkHasUnsavedChanges
    }}>
      {children}
    </EditingContext.Provider>
  );
};

// Contexte pour piloter le panneau gauche (onglet actif + surbrillance de guidage)
export const LeftPanelContext = createContext(null);

export const LeftPanelContextProvider = ({ children }) => {
  const [activeView, setActiveView] = useState('elements');
  // { view: 'elements' | 'rubrics' | 'standardReports', section: string | null, ts: number } ou null
  // section : clé d'une section de l'encodage (ex. 'personnesPhysiques') à mettre seule en avant
  const [highlightTarget, setHighlightTarget] = useState(null);

  // Déclenche une surbrillance temporaire sur la vue cible du panneau gauche
  const triggerHighlight = useCallback((view, section = null) => {
    setActiveView(view);
    setHighlightTarget({ view, section, ts: Date.now() });
  }, []);

  const clearHighlight = useCallback(() => {
    setHighlightTarget(null);
  }, []);

  return (
    <LeftPanelContext.Provider value={{
      activeView,
      setActiveView,
      highlightTarget,
      triggerHighlight,
      clearHighlight
    }}>
      {children}
    </LeftPanelContext.Provider>
  );
};

// Contexte partageant en direct les éléments encodés (panneau Encodage) avec les formulaires de rubrique
// reportElements : { number, year, unit, reportType, indice, intervenants, faits, personnesPhysiques,
// personnesMorales, objets, vehicules } ou null
export const ReportElementsContext = createContext(null);

export const ReportElementsContextProvider = ({ children }) => {
  const [reportElements, setReportElements] = useState(null);

  return (
    <ReportElementsContext.Provider value={{ reportElements, setReportElements }}>
      {children}
    </ReportElementsContext.Provider>
  );
};

// Contexte du glisser-déposer d'une rubrique ou d'un canevas du panneau gauche (Rubrics / StandardReports)
// vers l'édition (Edit) ou le sommaire (Summary)
export const RubricDragContext = createContext(null);

export const RubricDragContextProvider = ({ children }) => {
  // Titre de l'élément en cours de glissement, sinon null
  const [draggedRubricLabel, setDraggedRubricLabel] = useState(null);
  // Ajout de l'élément glissé, fourni par le panneau source : (beforeRubricId | null) => void
  const insertRef = useRef(null);

  const startRubricDrag = useCallback((event, label, insert) => {
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData(RUBRIC_DRAG_TYPE, label);
    insertRef.current = insert;
    // Différé : modifier le DOM pendant dragstart peut interrompre le glissement sous Chrome
    setTimeout(() => {
      if (insertRef.current === insert) setDraggedRubricLabel(label);
    }, 0);
  }, []);

  const endRubricDrag = useCallback(() => {
    insertRef.current = null;
    setDraggedRubricLabel(null);
  }, []);

  const dropRubric = useCallback((beforeRubricId) => {
    const insert = insertRef.current;
    endRubricDrag();
    if (insert) insert(beforeRubricId);
  }, [endRubricDrag]);

  // dragend remonte depuis l'élément source, y compris quand le glissement est annulé (Échap, hors zone)
  useEffect(() => {
    document.addEventListener('dragend', endRubricDrag);
    return () => document.removeEventListener('dragend', endRubricDrag);
  }, [endRubricDrag]);

  return (
    <RubricDragContext.Provider value={{
      draggedRubricLabel,
      startRubricDrag,
      dropRubric
    }}>
      {children}
    </RubricDragContext.Provider>
  );
};

// Contexte reliant le menu « Options » de la barre du PV (Up) aux outils du document (Read / Edit)
export const DocumentToolsContext = createContext(null);

// Rechercher se fait dans la vue affichée (Lecture ou Édition), Remplacer toujours en Édition
const FIND_VIEWS = ['read', 'edit'];

export const DocumentToolsContextProvider = ({ children }) => {
  const { mode: viewMode, handleModeChange } = useContext(ModeContext);
  const { report } = useContext(ReportContext);
  // { mode: 'find' | 'replace', view: 'read' | 'edit', ts: number } quand le panneau
  // Rechercher / Remplacer est ouvert, sinon null
  const [findReplaceRequest, setFindReplaceRequest] = useState(null);
  // Saisie commune aux panneaux des deux vues : { query, replacement, matchCase, wholeWord }
  const findStateRef = useRef(null);
  // Change à chaque demande de suppression de toutes les rubriques (0 = aucune demande)
  const [deleteAllRubricsRequest, setDeleteAllRubricsRequest] = useState(0);

  const openFindReplace = useCallback((mode) => {
    const toolMode = mode === 'replace' ? 'replace' : 'find';
    let targetView = 'edit';
    if (toolMode === 'find') targetView = FIND_VIEWS.includes(viewMode) ? viewMode : 'read';
    if (viewMode !== targetView) {
      handleModeChange(targetView);
      if (report) {
        logAction(LOG_ACTIONS.REPORT_MODE_CHANGE, formatReportNumber(report.number, report.year), {
          ancienMode: viewMode,
          nouveauMode: targetView
        });
      }
    }
    setFindReplaceRequest({ mode: toolMode, view: targetView, ts: Date.now() });
  }, [viewMode, handleModeChange, report]);

  const closeFindReplace = useCallback(() => {
    setFindReplaceRequest(null);
  }, []);

  // Changer de vue (boutons Édition / Lecture) ferme le panneau de l'autre vue
  useEffect(() => {
    setFindReplaceRequest(current => (
      current && current.view !== viewMode ? null : current
    ));
  }, [viewMode]);

  const requestDeleteAllRubrics = useCallback(() => {
    setDeleteAllRubricsRequest(Date.now());
  }, []);

  return (
    <DocumentToolsContext.Provider value={{
      findReplaceRequest,
      openFindReplace,
      closeFindReplace,
      findStateRef,
      deleteAllRubricsRequest,
      requestDeleteAllRubrics
    }}>
      {children}
    </DocumentToolsContext.Provider>
  );
};

// Contexte pour gérer la communication avec GuardIA
export const GuardiaContext = createContext(null);

export const GuardiaContextProvider = ({ children }) => {
  const [activeView, setActiveView] = useState('summary'); // Vue active par défaut
  const [guardiaMessage, setGuardiaMessage] = useState(''); // Message à ajouter dans l'insert
  const [citations, setCitations] = useState([]); // Array des citations avec métadonnées

  // Fonction pour changer la vue vers GuardIA
  const switchToGuardia = useCallback(() => {
    setActiveView('guardia');
  }, []);

  // Fonction pour ajouter du texte dans l'insert de GuardIA (legacy)
  const addTextToGuardiaInput = useCallback((text) => {
    setGuardiaMessage(text);
    // S'assurer que la vue GuardIA est active
    setActiveView('guardia');
  }, []);

  // Fonction pour effacer le message après qu'il ait été utilisé
  const clearGuardiaMessage = useCallback(() => {
    setGuardiaMessage('');
  }, []);

  // Fonction pour changer la vue active
  const changeActiveView = useCallback((view) => {
    setActiveView(view);
  }, []);

  // Fonction pour basculer vers GuardIA en mode chat
  const switchToGuardiaChat = useCallback(() => {
    setActiveView('guardia');
    // Le mode chat sera géré par GuardiaRight via l'état guardiaMode
  }, []);

  // Fonction pour ajouter une citation avec métadonnées
  const addCitation = useCallback((citation) => {
    const newCitation = {
      id: Date.now().toString(),
      title: citation.title,
      visibleText: citation.visibleText,
      fullText: citation.fullText,
      timestamp: new Date().toISOString()
    };

    setCitations(prev => [...prev, newCitation]);
    setActiveView('guardia'); // Basculer vers GuardIA

    return newCitation.id;
  }, []);

  // Fonction pour supprimer une citation
  const removeCitation = useCallback((citationId) => {
    setCitations(prev => prev.filter(citation => citation.id !== citationId));
  }, []);

  // Fonction pour obtenir le texte complet des citations (pour l'IA)
  const getCitationsFullText = useCallback(() => {
    return citations.map(citation => citation.fullText).join(' ');
  }, [citations]);

  // Fonction pour vider toutes les citations
  const clearCitations = useCallback(() => {
    setCitations([]);
  }, []);

  return (
    <GuardiaContext.Provider value={{
      activeView,
      guardiaMessage,
      citations,
      switchToGuardia,
      addTextToGuardiaInput,
      clearGuardiaMessage,
      changeActiveView,
      switchToGuardiaChat,
      addCitation,
      removeCitation,
      getCitationsFullText,
      clearCitations
    }}>
      {children}
    </GuardiaContext.Provider>
  );
};
